"use client";

/**
 * Page chrome for the public use-case / integration landing pages.
 *
 * Frames the page body with the shared <LandingNav> and a compact marketing
 * footer. The whole subtree follows the visitor's locale via the root
 * LocaleProvider — the page bodies are client components that resolve their
 * copy through useT(), so nav, footer and body stay in the same language.
 * Server pages stay server components (they export `metadata`) and render
 * their sections as children of this client shell.
 */

import Link from "next/link";
import { BrandLogo } from "@/components/brand";
import { LandingNav } from "@/components/landing-nav";
import { PUBLIC_EMAIL } from "@/lib/contact";
import { COMPANY } from "@/lib/company";
import { useT } from "@/i18n";

function MarketingFooter() {
  const t = useT();
  const columns: { title: string; titleHref?: string; links: { label: string; href: string }[] }[] = [
    {
      title: t("mk.footer.useCases"),
      titleHref: "/use-cases",
      links: [
        { label: t("mk.footer.links.supportDemo"), href: "/use-cases/support" },
        { label: t("mk.footer.links.engineeringDemo"), href: "/use-cases/engineering" },
        { label: t("mk.footer.links.ticketQuestions"), href: "/use-cases/slack-jira-ticket-questions" },
        { label: t("mk.footer.links.approvedReplies"), href: "/use-cases/freshdesk-jira-approved-replies" },
        { label: t("mk.footer.links.remediation"), href: "/use-cases/support-to-engineering-remediation" },
        { label: t("mk.footer.links.approvalLedger"), href: "/use-cases/ai-agent-approval-ledger" },
        { label: t("mk.footer.links.deployment"), href: "/use-cases/ai-deployment-governance" },
        { label: t("mk.footer.links.hrGoverned"), href: "/use-cases/hr-governed-answers" },
      ],
    },
    {
      title: t("mk.footer.integrations"),
      titleHref: "/integrations",
      links: [
        // Product names — not translated.
        { label: "Slack", href: "/integrations/slack" },
        { label: "Microsoft Teams", href: "/integrations/teams" },
        { label: "Jira", href: "/integrations/jira" },
        { label: "Freshdesk", href: "/integrations/freshdesk" },
        { label: "Odoo Helpdesk", href: "/integrations/odoo" },
        { label: "TOPdesk", href: "/integrations/topdesk" },
        { label: "AFAS Profit", href: "/integrations/afas" },
        { label: "Youforce", href: "/integrations/youforce" },
        { label: "GitHub", href: "/integrations/github" },
        { label: "GitLab", href: "/integrations/gitlab" },
        { label: "Argo", href: "/integrations/argo" },
        { label: "FC Agent", href: "/integrations/fc-agent" },
      ],
    },
    {
      title: t("mk.footer.resources"),
      links: [
        { label: t("mk.footer.links.product"), href: "/product" },
        { label: t("mk.footer.links.changeValidation"), href: "/product/change-validation" },
        { label: t("mk.footer.links.businessValue"), href: "/business-value" },
        { label: t("mk.footer.links.pricing"), href: "/pricing" },
        { label: t("mk.footer.links.docs"), href: "/docs/en" },
        { label: t("mk.footer.links.security"), href: "/security" },
        { label: t("mk.footer.links.about"), href: "/about" },
        { label: t("mk.footer.links.bookDemo"), href: "/demo" },
        { label: t("mk.footer.links.contact"), href: "/contact" },
      ],
    },
  ];

  return (
    <footer className="bg-bg-base">
      <div className="mx-auto max-w-6xl px-5 sm:px-6 py-14">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          <div className="col-span-2 md:col-span-1">
            <Link href="/" className="inline-flex">
              <BrandLogo size={20} wordmarkClassName="text-[15px]" />
            </Link>
            <p className="mt-4 text-[12px] text-fg-muted leading-relaxed">
              {t("mk.footer.tagline")}
            </p>
          </div>
          {columns.map((col) => (
            <div key={col.title}>
              <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-fg-muted">
                {col.titleHref ? (
                  <Link href={col.titleHref} className="hover:text-fg-bright transition">
                    {col.title}
                  </Link>
                ) : (
                  col.title
                )}
              </div>
              <ul className="mt-3 space-y-2 text-[13px] text-fg-dim">
                {col.links.map((l) => (
                  <li key={l.href}>
                    <Link href={l.href} className="hover:text-fg-bright transition">
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="mt-10 pt-6 border-t border-line-subtle text-[12px] text-fg-muted">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
            <span>© {new Date().getFullYear()} FixControl. {t("mk.footer.rights")}</span>
            <a href={`mailto:${PUBLIC_EMAIL}`} className="hover:text-fg-bright transition">
              {PUBLIC_EMAIL}
            </a>
          </div>
          <p className="mt-3 text-[11px] text-fg-muted">
            {COMPANY.chamberOfCommerceNumber && COMPANY.vatId
              ? t("mk.footer.legal", {
                  legal: COMPANY.legalName,
                  kvk: COMPANY.chamberOfCommerceNumber,
                  vat: COMPANY.vatId,
                })
              : t("mk.footer.legalPreRegistration", { legal: COMPANY.legalName })}
          </p>
        </div>
      </div>
    </footer>
  );
}

export function MarketingShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-bg-base text-fg">
      <LandingNav />
      <main>{children}</main>
      <MarketingFooter />
    </div>
  );
}
